import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { ProductCardV2 } from '@garnish/commerce';
import { theme } from '@garnish/constants';
import { BodyText } from '@garnish/core';
import { ContentfulImage, ContentfulImageTransformOptions, Image } from '@garnish/media';

import { MenuCategoriesGrid, type useBundleContent } from '@order/components-menu';

const FallbackImage = require('./assets/fallback.webp');

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentBundleEntryPointCard = memo((props: Props) => {
  const { content } = props;

  const {
    name,
    description,
    entryPointImageUrl,
    entryPointImageA11y,
    entryPointProtein,
    entryPointCalories,
    entryPointPrice,
    entryPointCta,
  } = content;

  const { formatMessage } = useIntl();

  // ─── Derived ─────────────────────────────────────────────────────────

  const hasNutritionData = Boolean(entryPointProtein || entryPointCalories);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <MenuCategoriesGrid.ProductCardLink href={BUNDLE_HREF} productName={name} isNavigationEnabled>
      <ProductCardV2.Container canHover style={styles.container}>
        {entryPointImageUrl ? (
          <ContentfulImage
            baseUrl={entryPointImageUrl}
            options={IMAGE_OPTIONS}
            alt={entryPointImageA11y ?? name}
            style={styles.image}
          />
        ) : (
          <Image
            source={FallbackImage}
            alt={formatMessage(messages.fallbackImageAlt)}
            style={styles.image}
          />
        )}

        <ProductCardV2.Heading>{name}</ProductCardV2.Heading>
        <ProductCardV2.Description>{description}</ProductCardV2.Description>

        {hasNutritionData ? (
          <ProductCardV2.NutritionData.Stack>
            {entryPointProtein ? (
              <ProductCardV2.NutritionData.Item
                value={entryPointProtein}
                label={formatMessage(messages.proteinLabel)}
              />
            ) : null}

            {entryPointCalories ? (
              <ProductCardV2.NutritionData.Item
                value={entryPointCalories}
                label={formatMessage(messages.caloriesLabel)}
              />
            ) : null}
          </ProductCardV2.NutritionData.Stack>
        ) : null}

        <View style={styles.footer}>
          {entryPointPrice ? (
            <BodyText sizeMatch={['14']} style={styles.price}>
              {entryPointPrice}
            </BodyText>
          ) : null}

          <View style={styles.cta}>
            <BodyText sizeMatch={['14']} style={styles.ctaText}>
              {entryPointCta}
            </BodyText>
          </View>
        </View>
      </ProductCardV2.Container>
    </MenuCategoriesGrid.ProductCardLink>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const BUNDLE_HREF = '/bundle';
const IMAGE_OPTIONS: Partial<ContentfulImageTransformOptions> = { fm: 'webp', w: 400 };

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  proteinLabel: {
    defaultMessage: 'protein',
    description: 'Menu | Bundle entry point card | Protein label',
  },
  caloriesLabel: {
    defaultMessage: 'cal',
    description: 'Menu | Bundle entry point card | Calories label',
  },
  fallbackImageAlt: {
    defaultMessage: 'An assortment of sweetgreen plates',
    description: 'Menu | Bundle entry point card | Fallback image alt',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    rowGap: theme.spacing['2'],
  },
  image: {
    alignSelf: 'stretch',
    height: 343,
    marginHorizontal: -theme.spacing['2'],
    marginBottom: theme.spacing['2'],
    borderRadius: theme.radius.large,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: theme.spacing['4'],
    marginTop: 'auto',
    paddingTop: theme.spacing['4'],
  },
  price: {
    flexShrink: 1,
    color: theme.colors.TEXT_COLOR,
  },
  cta: {
    marginLeft: 'auto',
    paddingVertical: theme.spacing['3'],
    paddingHorizontal: theme.spacing['6'],
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.KALE,
  },
  ctaText: {
    color: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  content: ReturnType<typeof useBundleContent>['content'];
};
